<script setup lang="ts">
/*
 * Canonical card. Two variants from the prototype:
 *  - plain  → 1px app-border, 16–20px radius (use as `radius="lg"` or "xl")
 *  - shell  → 24px radius + 4px gradient left accent (the .card-shell look,
 *    used for big run/schedule cards in the Payroll Module)
 *
 * Renders title/subtitle/header-actions only when slots/props are given —
 * the bare card with no header is also valid.
 */
type Variant = 'plain' | 'shell';
type Radius = 'lg' | 'xl' | 'card';

withDefaults(
  defineProps<{
    title?: string;
    subtitle?: string;
    variant?: Variant;
    radius?: Radius;
    padding?: 'none' | 'sm' | 'md' | 'lg';
  }>(),
  { variant: 'plain', radius: 'xl', padding: 'md' },
);

const radiusClass = { lg: 'rounded-2xl', xl: 'rounded-3xl', card: 'rounded-card' } as const;
const padClass = { none: '', sm: 'p-4', md: 'p-6', lg: 'p-8' } as const;
</script>

<template>
  <section
    :class="[
      'relative overflow-hidden border border-app-border bg-app-surface',
      variant === 'shell' ? 'card-shell rounded-card' : radiusClass[radius],
    ]"
  >
    <header
      v-if="title || subtitle || $slots.headerActions"
      :class="[
        'flex items-start justify-between gap-4',
        padClass[padding],
        $slots.default ? 'pb-3' : '',
      ]"
    >
      <div class="flex flex-col gap-0.5">
        <h3 v-if="title" class="text-lg font-semibold text-app-fg">{{ title }}</h3>
        <p v-if="subtitle" class="text-md text-app-muted">{{ subtitle }}</p>
      </div>
      <div v-if="$slots.headerActions" class="flex items-center gap-2">
        <slot name="headerActions" />
      </div>
    </header>
    <div
      v-if="$slots.default"
      :class="[padClass[padding], title || subtitle || $slots.headerActions ? 'pt-0' : '']"
    >
      <slot />
    </div>
  </section>
</template>

<style scoped>
.card-shell::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(
    180deg,
    var(--color-brand-soft) 0%,
    var(--color-primary) 55%,
    var(--color-brand-dark) 100%
  );
}
</style>
